import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { coreAnimations } from '../_shared/animations/core.anitmation';
import { AuthenticationService } from 'app/_shared/services';
import { Subscription, combineLatest, of, forkJoin } from 'rxjs';
import * as Models from 'app/_shared/models/models-index';
import moment from 'moment';
//import { SummaryMetricsByDay } from './shared/summaryMetricsByDay.model';
import { OutboundSummaryMetricsByDay } from './shared/outboundSummaryMetricsByDay.model';
import { OutboundSummaryMetrics } from './shared/outboundSummaryMetrics.model';
import { FilterService, FilterMonth, FilterRetailer, FilterProgram } from './shared/filter.service';
import { ReportsService } from './shared/reports.service';
import { map, switchMap, startWith } from 'rxjs/operators';
import { ChartOptions } from 'chart.js';
import { RetailersService } from 'app/_shared/services/api/api-index';
import { DecimalPipe, PercentPipe, CurrencyPipe } from '@angular/common';
import * as Utilities from 'app/_shared/utilities/utilities-index';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CardChartModel } from './card-chart/cardChart.model';
import { BillboardChartModel, BillboardStat } from './billboard-chart/billboardChart.model';
import { HoursMinutesPipe, SdRoundValuePipe } from 'app/_shared/pipes/pipes-index';
import { Enums, IEnums } from '../app.enums';
import { IFacetedFilters } from 'app/filterPanel/types';
import { DateRangeDialogComponent } from './../_shared/components/date-range-dialog/date-range-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
    selector: 'app-outbound-dashboard',
    templateUrl: './outboundDashboard.component.html',
    styleUrls: ['./outboundDashboard.component.scss'],
    animations: coreAnimations,
    providers: [HoursMinutesPipe, DecimalPipe, PercentPipe, CurrencyPipe, SdRoundValuePipe],
    standalone: false
})
export class OutboundDashboardComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

  selectedMonthOrRange: FilterMonth;
  private _lastSelectedMonth: FilterMonth;
  customDateRange = 'custom';
  currentRetailer: FilterRetailer = { id: 0 };
  enums: IEnums = Enums;
  chartBlue = 'rgba(0, 153, 255, .6)';
  chartDataSetDefaults = { borderWidth: 1, borderColor: this.chartBlue, lineTension: 0.4 }
  filterWellInitialized = false;

  retailerAutocompleteControl = new UntypedFormControl();
  retailersFiltered$ = combineLatest([
    this.retailerAutocompleteControl.valueChanges.pipe(
      startWith('')
    ),
    this.retailersService.fetchAll().pipe(
      map(retailers => {
        const mapped = retailers.map(r => {
          return { id: r.id, name: r.name, displayName: `${r.name} (${r.code})` };
        });

        mapped.unshift({ id: 0, name: '', displayName: '' }); // shell for 'All Retailers'
        return mapped;
      })
    )
  ]).pipe(
    switchMap(([filter, retailers]) => {
      const normalize = (s: string): string => s?.trim().toLowerCase();

      const filterValue = typeof filter === 'string' ? filter : '';
      const normalizedFilter = normalize(filterValue);

      if (!normalizedFilter) {
        return of(retailers);
      }

      const filtered = retailers.filter(r => normalize(r.displayName).includes(normalizedFilter));
      return of(filtered);
    })
  );

  billboardAnimation = { value: '*', params: { scale: '.9', delay: '300ms' } };
  filterState: Models.FilterState;
  dashboardTitle = '';

  months: FilterMonth[] = [];
  maxReportDate = moment().toDate();
  reportDateRange = new UntypedFormGroup({
    start: new UntypedFormControl(moment().startOf('month')),
    end: new UntypedFormControl(moment())
  });

  outboundSummaryMetricsByDaySub: Subscription;
  filterServiceUpdatedSub: Subscription;
  outboundSummaryMetrics: OutboundSummaryMetricsByDay;

  private billboardChartSharedOptions = <BillboardChartModel>{
    type: 'line',
    labels: [],
    datasets: [],
    // TODO: chartjs
    options: <ChartOptions>{
      responsive: true,
      animation: false,
      legend: {
        display: false
      },
      tooltips: {
        display: false,
        enabled: true
      },
      gridLines: {
        display: false
      },
      fill: {
        target: 'origin',
        above: this.chartBlue,
      },
      elements: {
        point: {
          radius: 0
        }
      },
      scales: {
        xAxes: {
          gridLines: {
            color: 'rgba(0, 0, 0, 0)',
            zeroLineColor: 'rgba(0, 0, 0, 0)'
          },
          display: false
        },
        yAxes: {
          gridLines: {
            color: 'rgba(0, 0, 0, 0)',
            zeroLineColor: 'rgba(0, 0, 0, 0)'
          },
          display: false
        }
      }
    },
    iconName: '',
    title: '',
    delta: 0,
    graphRange: {},
    footer: {},
    highlights: []
  };

  campaignDetailsBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'source',
    title: 'Outbound Records'
  };

  liveConnectionsBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'group',
    title: 'Live Connections'
  };

  appointmentsBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'calendar_today',
    title: 'Appointments'
  };

  messagesBillboardChart = <BillboardChartModel>{
    ...this.billboardChartSharedOptions,
    iconName: 'chat',
    title: 'Messages'
  };

  breakdownChart = <CardChartModel>{
    title: 'Call Breakdown',
    type: 'pie',
    labels: [],
    iconName: 'pie_chart',
    data:  {
      labels: ['Appointment', 'Customer to Schedule on Own', 'No Longer Owns Vehicle', 'Already Completed', 'Left Message', 'Unable to Leave Message', 'Miscellaneous'],
      datasets: [{
        data: [0, 0, 0, 0, 0, 0, 0],
        hoverOffset: 4,
        backgroundColor: [
          '#42a5f5', // Appointment
          '#66bb6a', // Customer to Schedule on Own
          '#ffa726', // No Longer Owns Vehicle
          '#ab47bc', // Already Completed
          '#26c6da', // Left Message
          '#ef5350', // Unable to Leave Message
          '#bdbdbd'  // Miscellaneous
        ],
        hoverBackgroundColor: [
          '#2e7ccf', // Appointment (darker)
          '#4a9e5a', // Customer to Schedule on Own (darker)
          '#e68a1a', // No Longer Owns Vehicle (darker)
          '#8b3a9a', // Already Completed (darker)
          '#1a9fb0', // Left Message (darker)
          '#d43a3a', // Unable to Leave Message (darker)
          '#9a9a9a'  // Miscellaneous (darker)
        ]
      }]
    },
    options: <ChartOptions>{
      responsive: true,
      aspectRatio: 1,
      animation: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const data = context.dataset.data;
              // Filter to only numbers for summing and percent calculation
              const numericData = Array.isArray(data) ? data.filter(v => typeof v === 'number') : [];
              const total: number = (numericData.reduce((prev, curr) => (typeof prev === 'number' ? prev : 0) + (typeof curr === 'number' ? curr : 0), 0)) as number;
              const value: number = (typeof data[context.dataIndex] === 'number' ? data[context.dataIndex] : 0) as number;
              const label = context.label;
              const itemPercent = (total && value) ? Math.round((value / total) * 100) : 0;
              return `${label} - ${value} : ${itemPercent}%`;
            }
          }
        }
      }
    }
  };

  callVolumeChart = <CardChartModel>{
    title: 'Call Stats',
    type: 'line',
    labels: [],
    iconName: 'show_chart',
    data: {
      labels: [],
      datasets: [{
        label: 'Call Volume',
        data: [],
        backgroundColor: 'rgba(0, 153, 255, 0.2)',
        borderColor: 'rgba(0, 153, 255, 1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3
      }]
    },
    options: <ChartOptions>{
      responsive: true,
      aspectRatio: 2,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            // You can add custom tooltip formatting here if needed
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          title: { display: false }
        },
        y: {
          grid: { color: '#eee' },
          beginAtZero: true,
          title: { display: false }
        }
      },
      elements: {
        point: { radius: 3 }
      }
    }
  };

  constructor(
    private filterService: FilterService,
    private reportsService: ReportsService,
    private retailersService: RetailersService,
    private authService: AuthenticationService,
    private percentPipe: PercentPipe,
    private currencyPipe: CurrencyPipe,
    private roundPipe: SdRoundValuePipe,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.months = this.filterService.getMonths().sort((a, b) => +b.startDate - +a.startDate);
    const currentMonth = this.months[0];
    this.selectedMonthOrRange = currentMonth;
    this._lastSelectedMonth = currentMonth;
    this.reportDateRange.setValue({
      start: moment(currentMonth.startDate),
      end: moment(currentMonth.endDate)
    });

    this.filterService.resetFilterState();

    const currentProgram = <FilterProgram>{ id: this.enums.program.recallConcierge };
    this.filterService.updateProgram(currentProgram);

    this.filterServiceUpdatedSub = this.filterService.updatedSubject.subscribe(filterState => {
      const filteredRetailer = filterState.filters.find(f => f.type === 'RetailerId');

      if (filteredRetailer) {
        let retailerId = parseInt(filteredRetailer.value, 10);
        this.retailersService.fetchRetailer(retailerId).subscribe(retailer => {

          if (retailer) {
            this.dashboardTitle = retailer.name;
          }
        });
        // if specific retailer, we aren't using the filter well, so bypass it
        this.filterWellInitialized = true;
      } else {
        this.dashboardTitle = '';
      }

      this.filterState = { ...filterState };
      this.loadDashboard();
    });

  }

  ngOnDestroy(): void {
    //this.outboundSummaryMetricsByDaySub.unsubscribe();
    this.filterServiceUpdatedSub.unsubscribe();
  }

  isRetailerUser(): boolean {
    return this.authService.isRetailerUser();
  }

  changeRetailer(id: number): void {
    const retailer = <FilterRetailer>{ id: id };

    this.currentRetailer = retailer;
    this.filterService.updateRetailer(retailer);
  }

  getRetailerAutcompleteDisplayValue(value: any): string {
    return value?.displayName;
  }

  changeMonth(filterMonth: FilterMonth | 'custom'): void {
    if (filterMonth === 'custom') {
      const dialogRef = this.dialog.open(DateRangeDialogComponent, {
        data: {
          start: this.reportDateRange.value.start.toDate(),
          end: this.reportDateRange.value.end.toDate()
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.reportDateRange.setValue({
            start: moment(result.start),
            end: moment(result.end)
          });

          const customMonth: FilterMonth & { custom?: boolean } = {
            label: this.getReportDateRangeLabel(),
            startDate: result.start,
            endDate: result.end,
            custom: true
          };

          const existingCustomIndex = this.months.findIndex(m => (m as any).custom);
          if (existingCustomIndex > -1) {
            this.months[existingCustomIndex] = customMonth;
          } else {
            this.months.unshift(customMonth);
          }

          this.selectedMonthOrRange = customMonth;
          this._lastSelectedMonth = customMonth;
          this.updateDashboardForDateRange();
        } else {
          this.selectedMonthOrRange = this._lastSelectedMonth;
        }
      });
      return;
    }

    const selectedMonth = filterMonth as FilterMonth;
    this.selectedMonthOrRange = selectedMonth;
    this._lastSelectedMonth = selectedMonth;
    const today = moment();
    const filterEnd = moment(selectedMonth.endDate).subtract(1, 'day');
    const isCurrentMonth = filterEnd.month() === today.month() && filterEnd.year() === today.year();

    this.reportDateRange.setValue({
      start: moment(selectedMonth.startDate),
      end: isCurrentMonth ? today : filterEnd
    });

    this.updateDashboardForDateRange();
  }

  getReportDateRangeLabel(): string {
    const format = (date: any) => moment(date).format('MM/DD/YYYY');
    return `${format(this.reportDateRange.value.start)} - ${format(this.reportDateRange.value.end)}`;
  }

  loadDashboard(): void {
    if (!this.filterWellInitialized)
      return;


    forkJoin(
      this.reportsService.getOutboundSummaryMetricsByDay(this.filterState),
      this.reportsService.getOutboundSummaryMetrics(this.filterState)
    ).subscribe(([summaryMetricsByDay, summaryMetrics]) => {
      this.outboundSummaryMetrics = summaryMetricsByDay;
      this.getCampaignDetailsBillboardChart(summaryMetricsByDay, summaryMetrics);
      this.getLiveConnectionsBillboardChart(summaryMetricsByDay, summaryMetrics);
      this.getAppointmentsBillboardChart(summaryMetricsByDay, summaryMetrics);
      this.getMessagesBillboardChart(summaryMetricsByDay, summaryMetrics);
      this.getCallBreakdownChart(summaryMetricsByDay);
      this.getCallVolumeChart(summaryMetricsByDay);

    });
  }

  onSelectedFilterChange($event: IFacetedFilters): void {
    this.filterService.updateRetailersAndOrgLookups($event.filters);
	}

  onFilterReset($event: IFacetedFilters): void {
    this.filterService.updateRetailersAndOrgLookups($event.filters);
	}

  onFilterWellInitialized($event: IFacetedFilters): void {
    this.filterWellInitialized = true;
    this.filterService.updateRetailersAndOrgLookups($event.filters);
	}

  updateDashboardForDateRange(): void {
    if (!this.reportDateRange.value.end) {
      this.reportDateRange.setValue({
        ...this.reportDateRange.value,
        end: moment().endOf('month')
      });
    }

    const range = {
      start: moment(this.reportDateRange.value.start).toDate(),
      end: moment(this.reportDateRange.value.end).add(1, 'day').toDate()
    };

    this.filterService.updateDates(range.start, range.end, 'Custom Range');
  }

  private getRateChange(current: number, previous: number): number {
    return Utilities.safeDivide((current - previous), previous);
  }

  private getCampaignDetailsBillboardChart(summaryMetricsByDay: OutboundSummaryMetricsByDay, summaryMetrics: OutboundSummaryMetrics): void {
    let highlights: BillboardStat[] = this.currentRetailer.id > 0 ?
      [{
        label: 'Estimated Costs:',
        value: this.currencyPipe.transform(summaryMetrics.estimatedCosts, 'USD', 'symbol', '1.0-0'),
        hasTooltip: false
      }, {
        label: 'Completed Calls to Date:',
        value: this.roundPipe.transform(summaryMetrics.completedCallsTotal),
        hasTooltip: true,
        tooltip: '*There can be multiple attempts per recall'
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }] :
      [{
        label: 'Completed Calls to Date:',
        value: this.roundPipe.transform(summaryMetrics.completedCallsTotal),
        hasTooltip: true,
        tooltip: '*There can be multiple attempts per recall'
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }];

    this.campaignDetailsBillboardChart = {
      ...this.campaignDetailsBillboardChart,
      labels: summaryMetricsByDay.records.map(r => r.date),
      datasets: [{ ...this.chartDataSetDefaults, data: summaryMetricsByDay.records.map(r => r.campaignSize) }],
      delta: this.getRateChange(summaryMetricsByDay.campaignSizeTotal, summaryMetricsByDay.previousCampaignSizeTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.roundPipe.transform(summaryMetrics.campaignSizeTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.roundPipe.transform(summaryMetrics.previousCampaignSizeTotal),
        hasTooltip: false
      },
      highlights: highlights
    }
  }

  private getLiveConnectionsBillboardChart(summaryMetricsByDay: OutboundSummaryMetricsByDay, summaryMetrics: OutboundSummaryMetrics): void {
    this.liveConnectionsBillboardChart = {
      ...this.liveConnectionsBillboardChart,
      labels: summaryMetricsByDay.records.map(r => r.date),
      datasets: [{...this.chartDataSetDefaults, data: summaryMetricsByDay.records.map(r => r.liveConnections) }],
      delta: this.getRateChange(summaryMetricsByDay.liveConnectionsTotal, summaryMetricsByDay.previousLiveConnectionsTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.roundPipe.transform(summaryMetrics.liveConnectionsTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.roundPipe.transform(summaryMetrics.previousLiveConnectionsTotal),
        hasTooltip: false
      },
      highlights: [{
        label: 'Live Connection Rate:',
        value: this.percentPipe.transform(summaryMetrics.liveConnectionRate),
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }]
    }
  }

  private getAppointmentsBillboardChart(summaryMetricsByDay: OutboundSummaryMetricsByDay, summaryMetrics: OutboundSummaryMetrics): void {
    this.appointmentsBillboardChart = {
      ...this.appointmentsBillboardChart,
      labels: summaryMetricsByDay.records.map(r => r.date),
      datasets: [{ ...this.chartDataSetDefaults, data: summaryMetricsByDay.records.map(r => r.appointments) }],
      delta: this.getRateChange(summaryMetricsByDay.appointmentsTotal, summaryMetricsByDay.previousAppointmentsTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.roundPipe.transform(summaryMetrics.appointmentsTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.roundPipe.transform(summaryMetrics.previousAppointmentsTotal),
        hasTooltip: false
      },
      highlights: [{
        label: 'Appts. Scheduled in OSS:',
        value: this.roundPipe.transform(summaryMetrics.apppointmentsOSS),
        hasTooltip: false
      }, {
        label: 'Appts. + Retailer Follow-up:',
        value: this.roundPipe.transform(summaryMetrics.apppointmentsFollowup),
        hasTooltip: false
      }, {
        label: 'Appointment Rate:',
        value: this.percentPipe.transform(summaryMetrics.appointmentRate),
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }]
    }
  }

  private getMessagesBillboardChart(summaryMetricsByDay: OutboundSummaryMetricsByDay, summaryMetrics: OutboundSummaryMetrics): void {
    this.messagesBillboardChart = {
      ...this.messagesBillboardChart,
      labels: summaryMetricsByDay.records.map(r => r.date),
      datasets: [{ ...this.chartDataSetDefaults, data: summaryMetricsByDay.records.map(r => r.voicemails) }],
      delta: this.getRateChange(summaryMetricsByDay.voicemailsTotal, summaryMetricsByDay.previousVoicemailsTotal),
      graphRange: {
        label: this.getReportDateRangeLabel(),
        value: this.roundPipe.transform(summaryMetrics.voicemailsTotal),
        hasTooltip: false
      },
      footer: {
        label: 'Previous:',
        value: this.roundPipe.transform(summaryMetrics.previousVoicemailsTotal),
        hasTooltip: false
      },
      highlights: [{
        label: 'Customer Returned Calls:',
        value: this.roundPipe.transform(summaryMetrics.inboundCallsTotal),
        hasTooltip: false
      }, {
        label: 'Message Rate:',
        value: this.percentPipe.transform(summaryMetrics.voicemailRate),
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }, {
        label: '',
        value: null,
        hasTooltip: false
      }]
    }
  }

  private getCallBreakdownChart(summaryMetricsByDay: OutboundSummaryMetricsByDay): void {
    this.breakdownChart = {
      ...this.breakdownChart,
      data: {
        datasets: [{
          data: [
            summaryMetricsByDay.appointmentsTotal,
            summaryMetricsByDay.customerToScheduleOnOwn, // cust schedule on own
            summaryMetricsByDay.noLongerOwnsVehicle, // no long owns vehicle
            summaryMetricsByDay.alreadyComplete, // already completed
            summaryMetricsByDay.leftMessage, // left message
            summaryMetricsByDay.unableToLeaveMessage, // unable to leave message
            summaryMetricsByDay.miscellaneousTotal
          ],
        }]
      }
    };
  }

  private getCallVolumeChart(summaryMetricsByDay: OutboundSummaryMetricsByDay): void {
    this.callVolumeChart = {
      ...this.callVolumeChart,
      data: {
        labels: summaryMetricsByDay.records.map(r => moment(r.date).format('MM/DD')),
        datasets: [{
          pointBackgroundColor: this.chartBlue,
          ...this.chartDataSetDefaults,
          data: summaryMetricsByDay.records.map(r => r.campaignSize),
        }]
      }
    };
  }
}
